import { useEffect } from "react";
import useDining from "../../../services/Order/useDining";

const isAvailable = (item) => item.reservationStatus === "N";

const getNumber = (item, isRoomOrder) => {
  if (isRoomOrder) {
    return item.roomNumber || item.roomNo || item.room_no || "";
  }

  return item.tableNo || item.table_no || "";
};

export default function TableSelectModal({
  open,
  onClose,
  onSelect,
  mobile,
  custMobile,
  orderType,
}) {
  const { tables, rooms, loading, fetchTables, fetchRooms } = useDining();

  const isRoomOrder =
    orderType === "room_order" ||
    orderType === "roomorder" ||
    orderType === "room order";

useEffect(() => {
  if (!open) return;

  const loadFreshData = async () => {
    try {
      if (isRoomOrder) {
        await fetchRooms({
          force: true,
          silent: false,
        });
      } else {
        await fetchTables({
          force: true,
          silent: false,
        });
      }
    } catch (error) {
      console.error(
        isRoomOrder
          ? "Fresh room fetch failed:"
          : "Fresh table fetch failed:",
        error,
      );
    }
  };

  loadFreshData();
}, [open, isRoomOrder]);

  if (!open) return null;

  const list = isRoomOrder
    ? Array.isArray(rooms)
      ? rooms
      : []
    : Array.isArray(tables)
      ? tables
      : [];

  const reserved = list.filter((item) => !isAvailable(item));
  const available = list.filter((item) => isAvailable(item));

  const handleItemClick = (item) => {
    const avail = isAvailable(item);
    const number = getNumber(item, isRoomOrder);

    if (avail) {
      onSelect({
        ...item,

        // Keep tableNo also because existing DineScreen/Payment flow uses tableNo
        tableNo: number,

        // Keep roomNumber also for room order
        roomNumber: number,

        orderId: "",
        mobile: mobile || custMobile || "",
        mode: "new",
      });

      onClose();
      return;
    }

    onSelect({
      ...item,

      tableNo: number,
      roomNumber: number,

      orderId:
        item.orderId ||
        item.orderid ||
        item.posOrderId ||
        item.billNo ||
        "",

      mobile:
        item.mobile ||
        item.custMobile ||
        item.customer_mobile ||
        item.customerMobile ||
        "",

      custName: item.custName || item.customer_name || "",

      mode: "reserved",
    });

    onClose();
  };

  return (
    <>
 
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 montserrat-font-1"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-3xl mx-4 montserrat-font-1 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center montserrat-font-1 justify-between mb-2">
          <h2 className="text-2xl font-bold text-gray-800 tracking-tight">
            {isRoomOrder ? "Select Room" : "Select Table"}
          </h2>

          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-all"
          >
            ✕
          </button>
        </div>

        {/* Legend */}
        <div className="flex gap-4 mb-5 montserrat-font-1">
          <span className="flex items-center gap-1.5 text-xs font-semibold text-gray-500">
            <span className="w-3 h-3 rounded-sm bg-amber-400 inline-block" />
            {isRoomOrder ? "Ongoing" : "Reserved"}
          </span>

          <span className="flex items-center gap-1.5 text-xs font-semibold text-gray-500">
            <span className="w-3 h-3 rounded-sm bg-emerald-500 inline-block" />
            Available
          </span>

          <span className="ml-auto text-xs font-mono text-gray-400">
            {loading
              ? "Loading..."
              : `${list.length} ${isRoomOrder ? "rooms" : "tables"} · ${
                  available.length
                } available`}
          </span>
        </div>

        {/* Loading */}
        {loading && (
          <div className="grid grid-cols-6 gap-3 animate-pulse">
            {Array(12)
              .fill(0)
              .map((_, i) => (
                <div key={i} className="rounded-xl bg-gray-100 h-16" />
              ))}
          </div>
        )}

        {/* No Data */}
        {!loading && list.length === 0 && (
          <p className="text-center text-sm text-gray-400 py-10">
            {isRoomOrder ? "No rooms found" : "No tables found"}
          </p>
        )}

        {/* Grid */}
        {!loading && list.length > 0 && (
          <div className="grid grid-cols-6 gap-3">
            {[...reserved, ...available].map((item) => {
              const avail = isAvailable(item);
              const number = getNumber(item, isRoomOrder);

              return (
                <button
                  key={item.id}
                  onClick={() => handleItemClick(item)}
                  className={`
                    flex flex-col items-center justify-center rounded-xl py-3 px-2
                    transition-all duration-150 select-none
                    ${
                      avail
                        ? "bg-emerald-500 hover:bg-emerald-600 cursor-pointer shadow-sm hover:shadow-md active:scale-95"
                        : "bg-amber-400 hover:bg-amber-500 cursor-pointer shadow-sm hover:shadow-md active:scale-95"
                    }
                  `}
                >
                  <span
                    className={`text-xl font-bold ${
                      avail ? "text-white" : "text-gray-800"
                    }`}
                  >
                    {number}
                  </span>

                  <span
                    className={`
                      mt-1.5 text-[10px] font-bold px-2 py-0.5 rounded-md
                      ${
                        avail
                          ? "bg-white/90 text-emerald-700"
                          : "bg-black/20 text-gray-900"
                      }
                    `}
                  >
                    {avail
                      ? isRoomOrder
                        ? "Take Order"
                        : "Available"
                      : "View Order"}
                  </span>

                  {!avail && item.custName && (
                    <span className="mt-1 text-[9px] text-gray-700 font-semibold truncate max-w-full px-1">
                      {item.custName}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        <p className="mt-5 text-xs text-gray-400 text-center montserrat-font-1">
          {isRoomOrder
            ? "Select a room to continue room order"
            : "Only available tables can be selected"}
        </p>
      </div>
    </div>
    </>
  );
}